import { useEffect, useState, useCallback } from "react";
import { Download, FileText, Search, RefreshCw, Filter, LogOut, ChevronLeft, ChevronRight, Loader2, Printer } from "lucide-react";
import { printBadge } from "@/lib/visitorBadge";
import { fmtHM } from "@/lib/format";
import { canWriteModule } from "@/lib/access";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail, API_BASE, BROWSER_TZ } from "@/lib/api";
import { PAGE_SIZE as SERVER_PAGE, totalFrom } from "@/components/LoadMoreBar";
import { toast } from "sonner";
import { TypeBadge } from "./AdminDashboard";
import { displayName } from "@/lib/format";

const todayIso = () => new Date().toISOString().slice(0, 10);

const PAGE_SIZE = 50;

// Compute a {from, to} date pair (YYYY-MM-DD local) for each preset.
function presetRange(preset) {
  const now = new Date();
  const yyyymmdd = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  if (preset === "today") {
    const s = yyyymmdd(now);
    return { from: s, to: s };
  }
  if (preset === "week") {
    // last 7 days including today
    const start = new Date(now);
    start.setDate(now.getDate() - 6);
    return { from: yyyymmdd(start), to: yyyymmdd(now) };
  }
  if (preset === "month") {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: yyyymmdd(start), to: yyyymmdd(now) };
  }
  if (preset === "year") {
    const start = new Date(now.getFullYear(), 0, 1);
    return { from: yyyymmdd(start), to: yyyymmdd(now) };
  }
  return { from: "", to: "" };
}

const isoToDateTimeRange = (from, to) => ({
  from: from ? new Date(from + "T00:00:00Z").toISOString() : undefined,
  to: to ? new Date(to + "T23:59:59Z").toISOString() : undefined,
});

export default function AdminLog() {
  const [filters, setFilters] = useState({
    q: "",
    status: "",
    type: "",
    range: "all",   // all | today | week | month | year | custom
    from: "",
    to: "",
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [pendingId, setPendingId] = useState(null);
  const [badgeCfg, setBadgeCfg] = useState(null);
  const { user } = useAuth();
  useEffect(() => { api.get("/settings/badge").then((r) => setBadgeCfg(r.data)).catch(() => {}); }, []);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [moreLoading, setMoreLoading] = useState(false);

  // Resolve the effective {from, to} based on the chosen preset.
  const effectiveDates = (() => {
    if (filters.range === "custom" || filters.range === "all") {
      return { from: filters.range === "custom" ? filters.from : "", to: filters.range === "custom" ? filters.to : "" };
    }
    return presetRange(filters.range);
  })();

  const buildParams = () => {
    const range = isoToDateTimeRange(effectiveDates.from, effectiveDates.to);
    return { q: filters.q || undefined, status: filters.status || undefined, type: filters.type || undefined, from: range.from, to: range.to, limit: SERVER_PAGE };
  };

  const loadMore = async () => {
    setMoreLoading(true);
    try {
      const res = await api.get("/visits", { params: { ...buildParams(), offset: rows.length } });
      setRows((prev) => [...prev, ...res.data]); setTotal(totalFrom(res));
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load"); }
    finally { setMoreLoading(false); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/visits", { params: buildParams() });
      setRows(res.data); setTotal(totalFrom(res));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.q, filters.status, filters.type, filters.range, filters.from, filters.to]);

  useEffect(() => {
    load();
  }, [load]);

  const exportFile = async (kind /* 'csv' | 'pdf' */) => {
    const setBusy = kind === "csv" ? setExporting : setExportingPdf;
    setBusy(true);
    try {
      const range = isoToDateTimeRange(effectiveDates.from, effectiveDates.to);
      const params = new URLSearchParams();
      if (filters.q) params.set("q", filters.q);
      if (filters.status) params.set("status", filters.status);
      if (filters.type) params.set("type", filters.type);
      if (range.from) params.set("from", range.from);
      if (range.to) params.set("to", range.to);
      const url = `${API_BASE}/visits/export.${kind}?${params.toString()}`;
      const token = localStorage.getItem("vlog_token");
      const res = await fetch(url, {
        headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), "X-Timezone": BROWSER_TZ },
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `logerd-signin-${todayIso()}.${kind}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setBusy(false);
    }
  };

  const forceCheckout = async (id, name) => {
    setPendingId(id);
    try {
      await api.patch(`/visits/${id}/force-checkout`);
      toast.success(`${name} checked out`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setPendingId(null);
    }
  };

  const set = (k) => (e) => { setPage(0); setFilters((f) => ({ ...f, [k]: e.target.value })); };

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);
  const pageStart = rows.length === 0 ? 0 : safePage * PAGE_SIZE + 1;
  const pageEnd = safePage * PAGE_SIZE + pageRows.length;

  return (
    <div className="p-6 md:p-10" data-testid="admin-log">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-slate-500">All Records</div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900">Visit Log</h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="log-refresh-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button
            onClick={() => exportFile("pdf")}
            disabled={exportingPdf}
            data-testid="export-pdf-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-100 disabled:opacity-60"
          >
            <FileText className="h-4 w-4" /> {exportingPdf ? "Building PDF…" : "Export PDF"}
          </button>
          <button
            onClick={() => exportFile("csv")}
            disabled={exporting}
            data-testid="export-csv-btn"
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition hover:shadow disabled:opacity-60"
          >
            <Download className="h-4 w-4" /> {exporting ? "Exporting…" : "Export CSV"}
          </button>
        </div>
      </header>

      {/* Filters */}
      <section className="rounded-xl border border-slate-200 bg-white p-4 mb-6">
        <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 mb-3">
          <Filter className="h-3.5 w-3.5" /> Filters
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={filters.q}
              onChange={set("q")}
              data-testid="log-search"
              placeholder="Search name, company, host…"
              className="w-full h-10 rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
            />
          </div>
          <select
            value={filters.status}
            onChange={set("status")}
            data-testid="log-status"
            className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
          >
            <option value="">All status</option>
            <option value="inside">Inside</option>
            <option value="checked_out">Checked out</option>
          </select>
          <select
            value={filters.type}
            onChange={set("type")}
            data-testid="log-type"
            className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
          >
            <option value="">All types</option>
            <option value="visitor">Visitor</option>
            <option value="contractor">Contractor</option>
          </select>
          <select
            value={filters.range}
            onChange={set("range")}
            data-testid="log-range"
            className="h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
          >
            <option value="all">All time</option>
            <option value="today">Today</option>
            <option value="week">This week (last 7 days)</option>
            <option value="month">This month</option>
            <option value="year">This year</option>
            <option value="custom">Custom range…</option>
          </select>
        </div>

        {filters.range === "custom" && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3 max-w-md" data-testid="log-custom-range">
            <label className="block">
              <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">From</span>
              <input
                type="date"
                value={filters.from}
                onChange={set("from")}
                data-testid="log-from"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </label>
            <label className="block">
              <span className="block mb-1 text-[11px] font-medium uppercase tracking-widest text-slate-500">To</span>
              <input
                type="date"
                value={filters.to}
                onChange={set("to")}
                data-testid="log-to"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm focus:border-primary focus:ring-4 focus:ring-primary/15 outline-none"
              />
            </label>
          </div>
        )}
      </section>

      <section className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="log-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
                <th className="px-5 py-3">Name</th>
                <th className="px-5 py-3">Type</th>
                <th className="px-5 py-3">Company</th>
                <th className="px-5 py-3">Host</th>
                <th className="px-5 py-3">Purpose</th>
                <th className="px-5 py-3">Plate</th>
                <th className="px-5 py-3">Officer</th>
                <th className="px-5 py-3">In</th>
                <th className="px-5 py-3">Out</th>
                <th className="px-5 py-3">Duration</th>
                <th className="w-[92px] px-2 py-3 text-center sticky right-0 bg-blue-100 text-blue-800 border-l border-blue-200 shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.15)]">Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={11} className="px-5 py-14 text-center text-slate-500">
                    No records found.
                  </td>
                </tr>
              )}
              {pageRows.map((v) => (
                <tr key={v.id} className="group border-t border-slate-100 hover:bg-slate-50" data-testid={`log-row-${v.id}`}>
                  <td className="px-5 py-3">
                    <div className="font-medium text-slate-900">{v.full_name}</div>
                    <div className="text-xs text-slate-500">{v.phone}{v.email ? ` · ${v.email}` : ""}</div>
                  </td>
                  <td className="px-5 py-3"><TypeBadge type={v.visitor_type} /></td>
                  <td className="px-5 py-3 text-slate-700">{v.company || "—"}</td>
                  <td className="px-5 py-3 text-slate-700">{v.host_name}</td>
                  <td className="px-5 py-3 text-slate-700 max-w-xs truncate" title={v.purpose}>{v.purpose}</td>
                  <td className="px-5 py-3 text-slate-700">{v.vehicle_plate || "—"}</td>
                  <td className="px-5 py-3 text-slate-700">
                    {displayName(v.officer_name) || <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">{new Date(v.checked_in_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</td>
                  <td className="px-5 py-3 text-slate-700 whitespace-nowrap">
                    {v.checked_out_at ? new Date(v.checked_out_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-emerald-700">Inside</span>}
                  </td>
                  <td className="px-5 py-3 text-slate-700">{fmtHM(v.duration_minutes)}</td>
                  <td className="w-[92px] px-2 py-2 text-center whitespace-nowrap sticky right-0 bg-blue-50 group-hover:bg-blue-100 border-l border-blue-200 shadow-[-8px_0_12px_-8px_rgba(15,23,42,0.15)]">
                    <div className="inline-flex items-center gap-1">
                      <button
                        onClick={() => badgeCfg && printBadge(v, badgeCfg)}
                        disabled={!badgeCfg}
                        title="Print badge"
                        aria-label="Print badge"
                        data-testid={`log-print-badge-${v.id}`}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-md text-primary hover:bg-primary hover:text-white disabled:opacity-40 transition"
                      >
                        <Printer className="h-4 w-4" />
                      </button>
                      {v.status === "inside" && canWriteModule(user, "visits") && (
                        <button
                          onClick={() => forceCheckout(v.id, v.full_name)}
                          disabled={pendingId === v.id}
                          title="Sign out"
                          aria-label="Sign out"
                          data-testid={`log-force-checkout-${v.id}`}
                          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-amber-700 hover:bg-amber-600 hover:text-white disabled:opacity-40 transition"
                        >
                          <LogOut className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50 text-xs text-slate-600">
          <div data-testid="log-pagination-info">
            Showing <b>{pageStart}</b>–<b>{pageEnd}</b> of <b>{rows.length}</b>{total > rows.length && <> loaded · <b>{total.toLocaleString()}</b> total</>}
            {total > rows.length && (
              <button onClick={loadMore} disabled={moreLoading} data-testid="log-load-more" className="ml-3 inline-flex items-center gap-1 rounded-md border border-primary/30 bg-white px-2.5 py-1 text-primary font-semibold hover:bg-primary/5 disabled:opacity-50">
                {moreLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : null} Load {Math.min(SERVER_PAGE, total - rows.length)} more
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(Math.max(0, safePage - 1))}
              disabled={safePage === 0}
              data-testid="log-prev-page"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-100 disabled:opacity-40 transition"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Prev
            </button>
            <button
              onClick={() => setPage(safePage + 1)}
              disabled={safePage >= totalPages - 1}
              data-testid="log-next-page"
              className="inline-flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 hover:bg-slate-100 disabled:opacity-40 transition"
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
